import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import Select from 'app/frontend/base/components/Select'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterClassSelect from 'app/frontend/base/components/CharacterClassSelect'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import CharacterClass, {
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import type { ApplicationFormValues } from './ApplicationForm'

const NEW_CHARACTER = 'new'

interface Props {
  form: UseFormReturn<ApplicationFormValues>
  characters: wowaudit.CharacterReference[]
  inputClassName?: string
  inputStyle?: React.CSSProperties
}

const ManualApplicationCharacterField = ({
  form,
  characters,
  inputClassName,
  inputStyle,
}: Props) => {
  const { t } = useTranslation()
  const characterId = form.watch('character_id')
  const addingNew = !characterId

  const options = [
    ...characters.map((character) => ({
      value: character.id.toString(),
      label: (
        <span
          className={cn(
            'flex items-center gap-2',
            classColours[character.characterClass].text,
          )}
        >
          <CharacterClassIcon
            size='small'
            characterClass={character.characterClass}
          />
          {character.name}
        </span>
      ),
      sortLabel: `0-${character.name}`,
    })),
    {
      value: NEW_CHARACTER,
      label: (
        <span className='flex items-center gap-2'>
          <Plus size={16} />
          {t('profile.manual-characters.add-new')}
        </span>
      ),
      sortLabel: '1',
    },
  ]

  return (
    <div className='flex flex-col gap-2'>
      {characters.length > 0 ? (
        <Select
          options={options}
          value={characterId ? characterId.toString() : NEW_CHARACTER}
          setValue={(value) =>
            form.setValue(
              'character_id',
              !value || value === NEW_CHARACTER ? null : parseInt(value),
              { shouldValidate: form.formState.isSubmitted },
            )
          }
          isSearchable={false}
          className={inputClassName}
          style={inputStyle}
        />
      ) : null}

      {addingNew ? (
        <div className='flex flex-wrap gap-2 *:grow *:basis-40'>
          <FormField
            control={form.control}
            name='new_character.name'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('profile.manual-characters.name')}
                    maxLength={12}
                    className={inputClassName}
                    style={inputStyle}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='new_character.surname'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('profile.manual-characters.surname')}
                    maxLength={12}
                    className={inputClassName}
                    style={inputStyle}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='new_character.character_class'
            render={({ field }) => (
              <FormItem>
                <CharacterClassSelect
                  value={field.value as CharacterClass | undefined}
                  setValue={field.onChange}
                  className={inputClassName}
                  style={inputStyle}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ) : null}
    </div>
  )
}

export default ManualApplicationCharacterField
